import * as angular from "angular";
import {CurrentUser} from "../models/user/user.model";
import {UserService} from "../common/user/user.service";
import {Group} from "../models/groups/group.model";
import {Student} from "../models/student.model";
import {GroupsService} from "../common/groups/groups.service";
import {Link} from "../models/link.model";
import {MessagingService, MessageObject} from "../common/messaging/messaging.service";
import {UserSessionService} from "../common/user-session/user-session.service";
import * as _ from "lodash";

const STUDENTS_PER_PAGE = 10;

const GROUP_INIT_DATA = "group";
const AVAILABLE_STUDENTS_INIT_DATA = "availableStudents";

enum StudentStatus {active, inactive, all}

export class DefaultGroupDetailController {
    crumbs: Link[];
    currentUser: CurrentUser;
    group: Group;
    tenantId: string;
    groupId: string;
    groupName: string;
    defaultGrades: string;
    pages: Student[][];
    currentPage: number = 1;
    messages: MessageObject;
    isAscendingSort: boolean;
    sortIcon: string;
    isDistrict: boolean;
    showError: boolean = false;
    studentStatus: StudentStatus;
    availableStudents: Student[];
    displayedStudentCount: number = 0;
    /* @ngInject */
    constructor(public $route: angular.route.IRouteService, userService: UserService, public $window: angular.IWindowService,
        SUCCEED: string, public groupsService: GroupsService, public messagingService: MessagingService,
        private userSessionService: UserSessionService,
        public FEATURE_TOGGLE: Object) {
        this.isAscendingSort = false;
        this.sortIcon = "ns-icon--sort";
        this.groupId = this.$route.current.params.groupId;
        this.tenantId = this.$route.current.params.tenantId;
        this.defaultGrades = this.$route.current.params.defaultGrades;
        userService.get().then(this.loadUser);
        this.messages = messagingService("students");
    }

    $onInit() {
        this.studentStatus = StudentStatus.all;
        this.group = this.$route.current.locals[GROUP_INIT_DATA];
        this.availableStudents = this.$route.current.locals[AVAILABLE_STUDENTS_INIT_DATA];

        if (this.availableStudents && this.availableStudents.length > 0) {
            this.sortStudents();
        }

        this.mapAvailableStudentStatuses();
        this.refreshDisplayedStudents();
        this.updateCrumbs();
    }

    hideError(): void { this.showError = false; }

    getDisplayInfo(): string {
        let count = this.currentPage * STUDENTS_PER_PAGE;
        let start = count - (STUDENTS_PER_PAGE -1);
        let end = (count > this.getDisplayedStudentCount()) ? this.getDisplayedStudentCount() : count;
        return `${start}-${end}`;
    }

    isStudentStatusFilter(studentStatus: string): boolean { 
        return this.studentStatus === StudentStatus[studentStatus]; 
    }

    filterStudentsByStatus(status: string): void {
        let studentStatus = StudentStatus[status];
        if(studentStatus === undefined) {
            this.showError = true;
        } else if(this.studentStatus !== studentStatus) {
            this.studentStatus = studentStatus;
            this.refreshDisplayedStudents();
        }
    }

    showPaginator(): boolean { return this.pages && this.pages.length > 1; }

    refreshDisplayedStudents(): void {
        this.pages = [[]];
        this.displayedStudentCount = 0;

        if(this.availableStudents) {
            this.availableStudents.forEach((student: Student) => {
                if(this.isStatusMatch(student, this.studentStatus)) {
                    let lastPage = this.pages[this.pages.length - 1];
                    if (lastPage.length >= STUDENTS_PER_PAGE) {
                        this.pages.push([]);
                        lastPage = this.pages[this.pages.length - 1];
                    }

                    lastPage.push(student);
                    this.displayedStudentCount++;
                }
            });
        }

        this.currentPage = Math.min(this.pages.length, this.currentPage);
    }

    isStatusMatch(student: Student, studentStatus: StudentStatus): boolean {
        if(!student) {
            return false;
        }

        let match;
        switch (StudentStatus[studentStatus]) {
            case StudentStatus[StudentStatus.active]:
                match = student.active;
                break;
            case StudentStatus[StudentStatus.inactive]:
                match = !student.active;
                break;
            case StudentStatus[StudentStatus.all]:
                match = true;
                break;
            default:
                match = false;
        }

        return match;
    }

    getDisplayedStudentCount(): number {
        return this.displayedStudentCount;
    }

    getPageOfStudents(): Student[] {
        return this.pages && this.pages[this.currentPage - 1];
    }

    hasDisplayedStudents(): boolean {
        return this.group && this.displayedStudentCount > 0;
    }

    sortStudents():void {
        this.isAscendingSort = !this.isAscendingSort;
        this.sortByStudentName(this.isAscendingSort);
    }

    getMessage(): string {
        let key = "noStudentsForStatus";
        if(!this.availableStudents || this.availableStudents.length === 0) {
            key = "noStudents";
        }

        return this.messages[key];
    }

    private updateCrumbs() {
        if (this.userSessionService.isDistrictMode()) {
            this.crumbs = [
                {
                    name: "Naviance Test Prep: District",
                    url: `${this.userSessionService.getDistrictTenantId()}`
                },
                {
                    name: `${this.userSessionService.getCurrentSchool().name}`,
                    url: `${this.userSessionService.getDistrictTenantId()}`
                },
                { name: this.group.groupName }
            ];
        } else {
            this.crumbs = [
                {
                    name: "Naviance Test Prep",
                    url: `${this.tenantId}`
                },
                { name: this.group.groupName }
            ];
        }
    }

    private sortByStudentName(isAscending: boolean) {
        this.sortIcon = "ns-icon--sort-up";
        this.availableStudents = _.sortBy(this.availableStudents, ["lastName", "firstName"]);

        if(!isAscending) {
            this.sortIcon = "ns-icon--sort-down";
            this.availableStudents.reverse();
        }

        this.refreshDisplayedStudents();
    }

    private loadUser = (currentUser: CurrentUser) => {
        this.currentUser = currentUser;
        this.updateCrumbs();
    };

    private mapAvailableStudentStatuses() {
        if(this.availableStudents && this.availableStudents.length > 0) {
            let activeStudentIds = [];
            if(this.group.students) {
                activeStudentIds = this.group.students.map(function(student) {
                    return student.userId;
                });
            }

            this.availableStudents.forEach((availableStudent: Student) => {
                availableStudent.active = activeStudentIds.indexOf(availableStudent.userId) >= 0;
            });
        }
    }
}



/** WEBPACK FOOTER **
 ** ./~/tslint-loader!./app/group-detail/default-group-detail.controller.ts
 **/